import * as React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Button,
  Modal,
  TextField,
  Typography,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ToastContainer, toast } from 'react-toastify';
import useMediaQuery from '@mui/material/useMediaQuery';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useAuth } from '../context/userContext.js';
import { useNavigate } from 'react-router-dom';
import api from '../../src/api.js';
import ROLES from '../common/roles.js';
import GavelIcon from '@mui/icons-material/Gavel';

const drawerWidth = 280;

export default function Sidebar({ mobileOpen, handleDrawerToggle }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // if you need it later
  const navigate = useNavigate();
  const [{ user, loading, name }] = useAuth();


  const [manageOpen, setManageOpen] = React.useState(false);
  const [reportsOpen, setReportsOpen] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [emailData, setEmailData] = React.useState({
    to: '',
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = React.useState([]);

  const canView = React.useCallback(
    (allowedRoles = []) => {
      if (!user) return false;
      return allowedRoles.includes(user.role) || user.role === ROLES.ADMIN;
    },
    [user]
  );

  // 🔹 After hooks: you can safely early-return
  if (loading) {
    return null; // or a small skeleton/spinner
  }

  // Optional: if no user, don't render sidebar at all
  if (!user) {
    return null;
  }

  const handleManageClick = () => setManageOpen(prev => !prev);
  const handleReportClick = () => setReportsOpen(prev => !prev);

  const handleModalOpen = () => setOpenModal(true);

  const handleModalClose = () => {
    setOpenModal(false);
    setEmailData({ to: '', subject: '', message: '' });
    setAttachments([]);
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendEmail = async () => {
    const formData = new FormData();
    formData.append('from', name || '');
    formData.append('to', emailData.to);
    formData.append('subject', emailData.subject);
    formData.append('message', emailData.message);

    attachments.forEach(file => {
      formData.append('attachments', file);
    });

    try {
      const response = await api.post('email/send', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 200) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'An error occurred while sending the email';
      toast.error(errorMessage);
    } finally {
      handleModalClose();
    }
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      setAttachments(prevFiles => [...prevFiles, file]);
    }
  };

  const removeAttachment = index => {
    setAttachments(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const drawer = (
    <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <List sx={{ flexGrow: 1 }}>
        {/* All Trackers */}
        {canView([ROLES.OWNER, ROLES.MANAGER, ROLES.SENIOR_MANAGER]) && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/alltrackers')}>
              <ListItemIcon>
                <TrackChangesIcon sx={{ color: '#ffffff' }} />
              </ListItemIcon>
              <ListItemText primary="All Trackers" />
            </ListItemButton>
          </ListItem>
        )}

        {/* My Dashboard */}
        {canView([ROLES.OWNER, ROLES.MANAGER, ROLES.SENIOR_MANAGER, ROLES.ADMIN]) && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/dashboard')}>
              <ListItemIcon>
                <DashboardIcon sx={{ color: '#ffffff' }} />
              </ListItemIcon>
              <ListItemText primary="My Dashboard" />
            </ListItemButton>
          </ListItem>
        )}

        {/* Manage Section */}
        {canView([ROLES.OWNER, ROLES.MANAGER, ROLES.SENIOR_MANAGER, ROLES.ADMIN]) && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={handleManageClick}>
                <ListItemIcon>
                  <ManageAccountsIcon sx={{ color: '#ffffff' }} />
                </ListItemIcon>
                <ListItemText primary="Manage" />
                {manageOpen ? (
                  <ExpandLessIcon sx={{ color: '#ffffff' }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={manageOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {canView([ROLES.ADMIN, ROLES.SENIOR_MANAGER]) && (
                  <ListItem disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => navigate('/manage/users')}>
                      <ListItemIcon>
                        <PeopleIcon sx={{ color: '#ffffff' }} />
                      </ListItemIcon>
                      <ListItemText primary="Users" />
                    </ListItemButton>
                  </ListItem>
                )}
                {canView([ROLES.MANAGER, ROLES.SENIOR_MANAGER]) && (
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate('/manage_tracker/assignment')}
                    >
                      <ListItemIcon>
                        <AssignmentIcon sx={{ color: '#ffffff' }} />
                      </ListItemIcon>
                      <ListItemText primary="Tracker Assignment" />
                    </ListItemButton>
                  </ListItem>
                )}
                {canView([ROLES.OWNER]) && (
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate('/manage/internal-trackers')}
                    >
                      <ListItemIcon>
                        <AssignmentTurnedInIcon sx={{ color: '#ffffff' }} />
                      </ListItemIcon>
                      <ListItemText primary="Internal Trackers" />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}

        {/* Reports Section */}
        {canView([ROLES.MANAGER, ROLES.SENIOR_MANAGER]) && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={handleReportClick}>
                <ListItemIcon>
                  <AssessmentIcon sx={{ color: '#ffffff' }} />
                </ListItemIcon>
                <ListItemText primary="REPORTS" />
                {reportsOpen ? (
                  <ExpandLessIcon sx={{ color: '#ffffff' }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => navigate('/reports/ageing_report')}>
                    <ListItemIcon>
                      <HistoryIcon sx={{ color: '#ffffff' }} />
                    </ListItemIcon>
                    <ListItemText primary="Ageing Report" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => navigate('/reports/detailed-report')}
                  >
                    <ListItemIcon>
                      <ArticleIcon sx={{ color: '#ffffff' }} />
                    </ListItemIcon>
                    <ListItemText primary="Detailed Report" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => navigate('/reports/user-report')}>
                    <ListItemIcon>
                      <ManageAccountsIcon sx={{ color: '#ffffff' }} />
                    </ListItemIcon>
                    <ListItemText primary="User Report" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => navigate('/reports/risk-profile')}>
                    <ListItemIcon>
                      <WarningAmberIcon sx={{ color: '#ffffff' }} />
                    </ListItemIcon>
                    <ListItemText primary="Risk Report" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => navigate('/reports/legal-update')}>
                    <ListItemIcon>
                      <AccountBalanceIcon sx={{ color: '#ffffff' }} />
                    </ListItemIcon>
                    <ListItemText primary="Legal Update Report" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </>
        )}

        {/* Legal Updates Section */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/legal-update')}>
            <ListItemIcon>
              <GavelIcon sx={{ color: '#ffffff' }} />
            </ListItemIcon>
            <ListItemText primary="LEGAL UPDATES" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#1e1e2f',
          color: '#ffffff'
        }
      }}
      ModalProps={{ keepMounted: true }}
    >
      {drawer}
    </Drawer>
  );
}































/*
<Box sx={{ p: 2, mt: 'auto', textAlign: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<MailIcon />}
                    sx={{
                        mb: 1,
                        width: '100%',
                        backgroundColor: '#4caf50',
                        '&:hover': {
                            backgroundColor: '#388e3c',
                            transform: 'scale(1.05)', // Animation effect
                        },
                        transition: 'transform 0.3s ease-in-out',
                    }}
                    onClick={handleModalOpen}
                >
                    Mail
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<ChatIcon />}
                    sx={{
                        width: '100%',
                        backgroundColor: '#f44336',
                        '&:hover': {
                            backgroundColor: '#d32f2f',
                            transform: 'scale(1.05)', // Animation effect
                        },
                        transition: 'transform 0.3s ease-in-out',
                    }}
                    onClick={() => navigate('/inhouse-text')}
                >
                    In-house Text
                </Button>
            </Box>

            <Modal
                open={openModal}
                onClose={handleModalClose}
                aria-labelledby="email-modal-title"
                aria-describedby="email-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        bgcolor: 'background.paper',
                        border: '1px solid #e0e0e0',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
             
                    <IconButton
                        onClick={handleModalClose}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'grey.500',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

             
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            mb: 2,
                            fontWeight: 'bold',
                            color: 'text.primary',
                            textAlign: 'center',
                        }}
                    >
                        Send Email
                    </Typography>

                  
                    <TextField
                        label="From"
                        name="from"
                        fullWidth
                        variant="outlined"
                        value={name}
                        onChange={handleInputChange}
                        sx={{
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#d0d0d0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#673ab7',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#673ab7',
                                },
                            },
                        }}
                    />
                    <TextField
                        label="To"
                        name="to"
                        fullWidth
                        variant="outlined"
                        value={emailData.to}
                        onChange={handleInputChange}
                        sx={{
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#d0d0d0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#673ab7',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#673ab7',
                                },
                            },
                        }}
                    />
                    <TextField
                        label="Subject"
                        name="subject"
                        fullWidth
                        variant="outlined"
                        value={emailData.subject}
                        onChange={handleInputChange}
                        sx={{
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#d0d0d0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#673ab7',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#673ab7',
                                },
                            },
                        }}
                    />
                    <TextField
                        label="Message"
                        name="message"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={emailData.message}
                        onChange={handleInputChange}
                        sx={{
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#d0d0d0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#673ab7',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#673ab7',
                                },
                            },
                        }}
                    />

                    
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<AttachFileIcon />}
                            sx={{
                                bgcolor: '#673ab7',
                                color: '#fff',
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: '#5e35b1',
                                },
                            }}
                        >
                            Attach File
                            <input type="file" hidden onChange={handleFileChange} />
                        </Button>
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<ImageIcon />}
                            sx={{
                                bgcolor: '#1e88e5',
                                color: '#fff',
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: '#1565c0',
                                },
                            }}
                        >
                            Attach Image
                            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                        </Button>
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<DescriptionIcon />}
                            sx={{
                                bgcolor: '#f4511e',
                                color: '#fff',
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: '#e64a19',
                                },
                            }}
                        >
                            Attach PDF
                            <input type="file" hidden accept="application/pdf" onChange={handleFileChange} />
                        </Button>
                    </Stack>

                    
                    <Box sx={{ mt: 2 }}>
                        {attachments.length > 0 ? (
                            attachments.map((file, index) => (
                                <Chip
                                    key={index}
                                    label={file.name}
                                    onDelete={() => removeAttachment(index)}
                                    sx={{
                                        margin: 0.5,
                                        bgcolor: 'grey.200',
                                        color: 'text.primary',
                                    }}
                                    deleteIcon={<CloseIcon />}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No attachments selected
                            </Typography>
                        )}
                    </Box>

                    
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSendEmail}
                        sx={{
                            mt: 2,
                            bgcolor: '#2e7d32',
                            color: '#fff',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#1b5e20',
                            },
                        }}
                    >
                        Send
                    </Button>
                </Box>
            </Modal>
*/